import { api } from './http';
import { API_ENDPOINTS } from '@/config/constants';
import { EventTrailsParams, EventTrailsResponse } from '@/types/eventTrails';
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
} from './accessTokenStore';
import { setLastLoginSuccessAt } from './authEpoch';

export type LoginPayload = {
  login: string;
  password: string;
};

export type Study = {
  id: number;
  name: string;
  description?: string;
  site_id?: number;
};

export interface UserFromApi {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
  full_name: string;
  email: string;
  user_roles: unknown[];
  user_sites: unknown[];
  studies: unknown[];
  user_settings: unknown[];
  current_study: Study | null;
  permitted_studies: Study[];
  roles: {
    id: number;
    name: string;
    description: string;
    json_permissions: string;
    study_id: number;
  }[];
}

export interface LoginApiResponse {
  user: string;
  exp: number;
}

export type RefreshTokenApiResponse = {
  access_token?: string;
  token_exp?: number;
} & Record<string, unknown>;

export async function login(payload: LoginPayload): Promise<UserFromApi> {
  try {
    // Avoid reusing a stale token across re-logins in the same tab.
    clearStoredAccessToken();

    await api.post<LoginApiResponse>(
      API_ENDPOINTS.LOGIN,
      { user: payload }
    );

    // Seed sessionStorage with the current access token.
    // We can't read HttpOnly cookies, but refresh returns the access token in JSON.
    // Best-effort: login should still succeed if this fails.
    try {
      await refreshToken();
    } catch {
      // ignore
    }

    // Fetch full user profile with retry to smooth out cookie propagation race.
    // Cookies may not be immediately available; retry a few times with short backoff.
    const maxAttempts = 5;
    const delays = [0, 200, 400, 600, 800];
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (delays[attempt]! > 0) {
        await new Promise(resolve => setTimeout(resolve, delays[attempt]));
      }
      try {
        const userProfile = await getCurrentUser();
        // Mark successful login so stale profile 401s (from requests before login) don't trigger reauth
        setLastLoginSuccessAt();
        return userProfile;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Failed to fetch user');
        const isUnauthorized =
          lastError.message === 'Unauthorized' ||
          lastError.message.includes('Missing token');
        if (!isUnauthorized || attempt === maxAttempts - 1) {
          throw lastError;
        }
      }
    }

    throw lastError ?? new Error('Login failed');
  } catch (error: unknown) {
    // Type guard for error with response (Axios-style)
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    // Preserve message from retry loop or other Error instances
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Login failed');
  }
}

export async function logout(): Promise<void> {
  try {
    await api.delete(API_ENDPOINTS.LOGOUT);
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Logout failed');
  } finally {
    // Ensure local token fallback is cleared even if the API call fails.
    clearStoredAccessToken();
  }
}

export async function refreshToken(
  studyId?: number
): Promise<RefreshTokenApiResponse> {
  try {
    const payload = studyId ? { study_id: studyId } : {};

    const storedAccessToken = getStoredAccessToken();
    const response = await api.post(
      API_ENDPOINTS.REFRESH_TOKEN,
      payload,
      storedAccessToken
        ? { headers: { Authorization: `Bearer ${storedAccessToken}` } }
        : undefined
    );

    // Parse access token to get expiration time
    if (response.data && response.data.access_token) {
      // Keep the latest access token for refresh fallback when HttpOnly cookie disappears.
      setStoredAccessToken(response.data.access_token);

      try {
        const tokenPayload = JSON.parse(
          atob(response.data.access_token.split('.')[1])
        ) as { exp?: number };
        if (tokenPayload.exp) {
          response.data.token_exp = tokenPayload.exp;
        }
      } catch {
        // Best-effort: token parsing failures shouldn't block refresh.
      }
    }

    return response.data as RefreshTokenApiResponse;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Token refresh failed');
  }
}

export async function getCurrentUser(): Promise<UserFromApi> {
  try {
    const res = await api.get(API_ENDPOINTS.USER_PROFILE);
    return res.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object'
    ) {
      const response = error.response as {
        status?: number;
        data?: { error?: string };
      };
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.data?.error) {
        if (response.data.error.includes('Missing token')) {
          throw new Error('Unauthorized');
        }
        throw new Error(response.data.error);
      }
    }
    throw new Error('Failed to fetch user');
  }
}

// Study Files API functions

// Types for validate meta functionality
export interface FileMetadata {
  filename: string;
  size: number;
  content_type: string;
}

export interface ServerValidationResult {
  filename: string;
  error?: string;
}

export interface ValidateMetaResponse {
  data: ServerValidationResult[];
}

export async function validateStudyFilesMeta(
  metadata: FileMetadata[]
): Promise<ServerValidationResult[]> {
  try {
    const response = await api.post(API_ENDPOINTS.STUDY_FILES_VALIDATE_META, {
      metadata,
    });

    // Extract data from the response structure
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }

    // Fallback for direct array response
    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to validate study files metadata');
  }
}

// Event Trails API functions

export async function getEventTrails(
  params?: EventTrailsParams
): Promise<EventTrailsResponse> {
  try {
    const response = await api.get(API_ENDPOINTS.EVENT_TRAILS, { params });
    return response.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object'
    ) {
      // Check for 401 status
      if ('status' in error.response && error.response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data &&
        typeof error.response.data.error === 'string'
      ) {
        throw new Error(error.response.data.error);
      }
    }
    throw new Error('Failed to fetch event trails');
  }
}
